// src/pages/ChartDetail.jsx
// Phase 4 step 14a-3: "Used in dashboards", "Add to dashboard", and a delete that
// asks when a dashboard shows the chart (CHART_IN_USE).
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit2, 
  Trash2, 
  Download, 
  Code2, 
  Share2,
  Eye,
  Calendar,
  Folder,
  Tag,
  BarChart3,
  Loader2,
  Clock,
  Globe,
  Lock,
  Copy,
  LayoutDashboard
} from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card.jsx';
import { apiCall } from '../utils/api';
import { isAnalystOrHigher } from '../utils/roles';
import { API_BASE_URL } from '../config';
import ScheduleExportModal from '../components/modals/ScheduleExportModal';
import ShareModal from '../components/modals/ShareModal';
import ConfirmDialog from '../components/modals/ConfirmDialog';
// Phase 3 step 10: charts made with the new builder are drawn here by
// ChartView (their saved type, formatting and matrix layout) and edited in
// the new builder; older charts keep the embed preview until step 11.
import ChartView from '../charts/ChartView';
import { editPath, isBuilderChart, viewProps } from './builder/savedChart';
// Downloads (fix after 10b): a new tab carries no login, so the PNG page is
// opened with a short-lived preview token (the same one the in-page preview
// uses); a table or matrix has no picture, so it downloads as CSV, built from
// the chart's own data with the saved column order.
import { chartData, deleteChart, duplicateChart } from '../charts/semanticApi';
// Phase 4 step 14a-3: dashboards. Deleting a chart a dashboard shows is refused
// by the server (CHART_IN_USE) and asked about; "Add to dashboard" appends a tile.
import ChartInUseDialog, { deleteChartWithGuard } from './dashboards/ChartInUseDialog';
import AddToDashboardDialog from './dashboards/AddToDashboardDialog';
import { copyFailureMessage } from './builder/savedChart';
import { csvFileName, toCsv } from '../charts/csv';
// Step 11: a chart from the previous builder shows what updating it would do; a converted one can be put back.
import { ConvertedNote, UpdateChartCard } from './ConversionCard';

const API_BASE = API_BASE_URL;

// ==========================================
// FILTER DISPLAY COMPONENTS
// ==========================================
function FilterBadge({ filter }) {
  const operatorSymbols = {
    '=': '=',
    '!=': '≠',
    'contains': '⊃'
  };

  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded-full text-sm">
      <span className="text-blue-300 font-medium">{filter.field}</span>
      <span className="text-blue-400/60">{operatorSymbols[filter.operator] || filter.operator}</span>
      <span className="text-blue-100">"{filter.value}"</span>
    </div>
  );
}

function FiltersDisplay({ filters, filterLogic, groupByField }) {
  if (!filters || filters.length === 0) {
    if (!groupByField) return null;
    
    // Show only group by
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded-full text-sm">
        <span className="text-purple-300">Grouped by:</span>
        <span className="text-purple-100 font-medium">{groupByField}</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        {filters.map((filter, index) => (
          <React.Fragment key={index}>
            <FilterBadge filter={filter} />
            {index < filters.length - 1 && (
              <span className="text-xs font-semibold text-blue-400 px-2">
                {filterLogic || 'AND'}
              </span>
            )}
          </React.Fragment>
        ))}
      </div>
      
      {groupByField && (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded-full text-sm">
          <span className="text-purple-300">Grouped by:</span>
          <span className="text-purple-100 font-medium">{groupByField}</span>
        </div>
      )}
    </div>
  );
}

export default function ChartDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const viewIncrementedRef = useRef(false);
  
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);  
  const [confirmDialog, setConfirmDialog] = useState({           
  isOpen: false,
  title: '',
  message: '',
  onConfirm: () => {}
});
  const [inUse, setInUse] = useState(null);            // 14a-3: { chartTitle, dashboards } while the server's refusal is shown
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [showAddToDashboard, setShowAddToDashboard] = useState(false);
  useEffect(() => {
    loadChart();
  }, [id]);

  async function loadChart() {
    setLoading(true);
    setError(null);
    
    try {
      const res = await apiCall(`/charts/${id}`);
      if (!res.ok) {
        throw new Error('Failed to load chart');
      }
      const data = await res.json();
      setChart(data);

      // Increment view count only once
      if (!viewIncrementedRef.current) {
        incrementViewCount();
        viewIncrementedRef.current = true;
      }
    } catch (err) {
      console.error('Failed to load chart:', err);
      setError(err.message || 'Failed to load chart');
    } finally {
      setLoading(false);
    }
  }

  function ChartPreviewIframe({ chart, apiBase }) {
  const [previewToken, setPreviewToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchToken() {
      // Public charts use their embed_token directly
      if (chart.visibility === 'public' && chart.embed_token) {
        setPreviewToken(chart.embed_token);
        setLoading(false);
        return;
      }
      
      // Private charts: fetch a short-lived preview token
      try {
        const res = await apiCall(`/charts/${chart.id}/preview-token`);
        if (res.ok) {
          const data = await res.json();
          setPreviewToken(data.preview_token);
        }
      } catch (err) {
        console.error('Failed to fetch preview token:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchToken();
  }, [chart.id, chart.visibility, chart.embed_token]);

  if (loading) return <div className="h-full flex items-center justify-center text-xs text-[#6a6a6a]">Loading preview...</div>;
  if (!previewToken) return <div className="h-full flex items-center justify-center text-xs text-[#6a6a6a]">Preview unavailable</div>;

  const src = `${apiBase}/embed/chart/${chart.id}?viz=${chart.chart_type}&theme=dark&token=${previewToken}`;

  return (
    <iframe
      src={src}
      width="100%"
      height="100%"
      frameBorder="0"
      title={chart.title}
    />
  );
}function ChartPreviewIframe({ chart, apiBase }) {
  const [previewToken, setPreviewToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchToken() {
      // Public charts use their embed_token directly
      if (chart.visibility === 'public' && chart.embed_token) {
        setPreviewToken(chart.embed_token);
        setLoading(false);
        return;
      }
      
      // Private charts: fetch a short-lived preview token
      try {
        const res = await apiCall(`/charts/${chart.id}/preview-token`);
        if (res.ok) {
          const data = await res.json();
          setPreviewToken(data.preview_token);
        }
      } catch (err) {
        console.error('Failed to fetch preview token:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchToken();
  }, [chart.id, chart.visibility, chart.embed_token]);

  if (loading) return <div className="h-full flex items-center justify-center text-xs text-[#6a6a6a]">Loading preview...</div>;
  if (!previewToken) return <div className="h-full flex items-center justify-center text-xs text-[#6a6a6a]">Preview unavailable</div>;

  const src = `${apiBase}/embed/chart/${chart.id}?viz=${chart.chart_type}&theme=dark&token=${previewToken}`;

  return (
    <iframe
      src={src}
      width="100%"
      height="100%"
      frameBorder="0"
      title={chart.title}
    />
  );
}

  async function incrementViewCount() {
    try {
      await apiCall(`/charts/${id}/increment-view`, {
        method: 'POST'
      });
    } catch (err) {
      // Fail silently - view counting is not critical
      console.error('Failed to increment view count:', err);
    }
  }

  const handleEdit = () => {
    navigate(editPath({ ...chart, id }));
  };

// 14a-3: a chart on a dashboard is refused by the server with the dashboards
// listed; ChartInUseDialog asks, and "Delete anyway" sends it again with force
// (the tiles stay, saying the chart is no longer available).
const runDelete = async (force) => {
  setDeleting(true);
  setDeleteError(null);
  try {
    const gone = await deleteChartWithGuard(deleteChart, Number(id), {
      force, onInUse: (dashboards) => setInUse({ chartTitle: chart.title, dashboards }),
    });
    if (gone) navigate('/charts');
  } catch (err) {
    setDeleteError(err && err.status === 403 ? "You don't have permission to delete charts." : "The chart couldn't be deleted. Try again.");
  } finally {
    setDeleting(false);
  }
};

const handleDelete = () => {
  setConfirmDialog({
    isOpen: true,
    title: 'Delete Chart?',
    message: `"${chart.title}" will be permanently deleted. This action cannot be undone.`,
    onConfirm: () => runDelete(false),
  });
};

  const [downloadError, setDownloadError] = useState(null);
  const tabular = isBuilderChart(chart || {}) && ['table', 'matrix'].includes(viewProps(chart).type);

  // The PNG is drawn by the embed page in a new tab. The tab is opened at once
  // (a tab opened after waiting is blocked as a pop-up), then sent to the page
  // with a preview token, which only opens this chart and expires in minutes.
  async function downloadPng() {
    setDownloadError(null);
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;   // the embed page gets no handle on this one (12b)
    try {
      const res = await apiCall(`/charts/${id}/preview-token`);
      if (!res.ok) throw new Error('token');
      const { preview_token: token } = await res.json();
      const viz = isBuilderChart(chart) ? '' : `&viz=${encodeURIComponent(chart.chart_type)}`;
      const url = `${API_BASE}/embed/chart/${id}?download=1${viz}&token=${encodeURIComponent(token)}`;
      if (tab) tab.location.href = url; else window.location.assign(url);
    } catch (err) {
      if (tab) tab.close();
      setDownloadError("The download couldn't start. Try again.");
    }
  }

  async function downloadCsv() {
    setDownloadError(null);
    try {
      const data = await chartData(chart.id);
      const columns = chart.presentation && Array.isArray(chart.presentation.columns) ? chart.presentation.columns : null;
      const blob = new Blob([toCsv(data.columns, data.rows, columns)], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = csvFileName(chart.title, chart.id);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setDownloadError("The data couldn't be downloaded. Try again.");
    }
  }

  const handleExport = () => (tabular ? downloadCsv() : downloadPng());

  // Step 10c: a copy to change for another team. A builder chart's copy opens
  // in the builder, ready for its filter to be changed; an older chart's copy
  // opens on its own page.
  const [duplicating, setDuplicating] = useState(false);
  const [copyError, setCopyError] = useState(null);
  async function handleDuplicate() {
    if (duplicating) return;
    setDuplicating(true);
    setCopyError(null);
    try {
      const copy = await duplicateChart(id);
      navigate(isBuilderChart(copy) ? editPath(copy) : `/charts/${copy.id}`);
    } catch (err) {
      setCopyError(copyFailureMessage(err));
    } finally {
      setDuplicating(false);
    }
  }

  const handleShare = () => {
  setShowShareModal(true);
};



  if (loading) {
    return (
      <div className="h-[calc(100vh-4rem)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (error || !chart) {
    return (
      <div className="space-y-6">
        <Button variant="secondary" onClick={() => navigate('/charts')}>
          <ArrowLeft className="w-4 h-4" />
          Back to Charts
        </Button>
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-sm text-red-400">{error || 'Chart not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/charts')}>
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-white">
              {chart.title}
            </h1>
            <div className="flex items-center gap-3 mt-2">
              <Badge variant="neutral">
                {chart.chart_type}
              </Badge>
              <Badge variant={chart.visibility === 'public' ? 'success' : 'neutral'}>
                {chart.visibility === 'public' ? (
                  <>
                    <Globe className="w-3 h-3" />
                    Public
                  </>
                ) : (
                  <>
                    <Lock className="w-3 h-3" />
                    Private
                  </>
                )}
              </Badge>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          {isAnalystOrHigher() && (
            <Button variant="secondary" onClick={handleEdit}>
              <Edit2 className="w-4 h-4" />
              Edit
            </Button>
          )}
          {isAnalystOrHigher() && (
            <Button variant="secondary" onClick={handleDuplicate} disabled={duplicating}
              title="Make a private copy to change, for example with another team's filter">
              <Copy className="w-4 h-4" />
              {duplicating ? 'Copying…' : 'Duplicate'}
            </Button>
          )}
          <Button variant="secondary" onClick={handleExport}>
            <Download className="w-4 h-4" />
            Export
          </Button>
          <Button variant="secondary" onClick={handleShare}>
            <Share2 className="w-4 h-4" />
            Share
          </Button>
          {isAnalystOrHigher() && isBuilderChart(chart) && (
            <Button variant="secondary" onClick={() => setShowAddToDashboard(true)} title="Put this chart on a dashboard">
              <LayoutDashboard className="w-4 h-4" />
              Add to dashboard
            </Button>
          )}
          {isAnalystOrHigher() && (
            <Button variant="danger" onClick={handleDelete} disabled={deleting}>
              <Trash2 className="w-4 h-4" />
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          )}
        </div>
      </div>

      {copyError && <p role="alert" className="-mt-2 mb-4 text-sm text-red-400">{copyError}</p>}
      {deleteError && <p role="alert" className="-mt-2 mb-4 text-sm text-red-400">{deleteError}</p>}

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Chart Preview (2/3 width) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Chart Embed */}
          <Card>
            <CardContent className="p-6">
              {isBuilderChart(chart) ? (
                <div className="space-y-3">
                  <div className="rounded-lg bg-[color:var(--chart-surface)] p-4">
                    <ChartView chartId={chart.id} height={440} {...viewProps(chart)} />
                  </div>
                  <ConvertedNote chart={chart} onReverted={(back) => setChart(back)} />
                </div>
              ) : (
                <div className="aspect-video border border-[#2a2a2a] rounded-lg overflow-hidden">
                  <ChartPreviewIframe chart={{ ...chart, id }} apiBase={API_BASE} />
                </div>
              )}

            </CardContent>
          </Card>

          {!isBuilderChart(chart) && <UpdateChartCard chart={{ ...chart, id: Number(id) }} onConverted={(c) => setChart(c)} />}

          {/* Embed Code */}
<Card>
  <CardHeader>
    <div className="flex items-center gap-2">
      <Code2 className="w-4 h-4 text-blue-500" />
      <CardTitle>Embed Code</CardTitle>
    </div>
  </CardHeader>
  <CardContent>
    {chart.visibility === 'public' && chart.embed_token ? (
      <>
        <code className="block p-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded text-xs text-[#a0a0a0] overflow-x-auto">
          <ChartPreviewIframe chart={chart} apiBase={API_BASE} />
        </code>
        <p className="text-xs text-[#6a6a6a] mt-2">
          This chart is public. Anyone with this URL can view it.
        </p>
      </>
    ) : (
      <>
        <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded text-xs text-amber-400">
          This chart is private. Make it public to get a shareable embed link.
        </div>
        <p className="text-xs text-[#6a6a6a] mt-2">
          Click "Share" to change visibility.
        </p>
      </>
    )}
  </CardContent>

</Card>
        </div>

        {/* Right Column - Metadata (1/3 width) */}
        <div className="space-y-6">
          {/* Chart Info */}
          <Card>
            <CardHeader>
              <CardTitle>Chart Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <InfoRow 
                icon={BarChart3} 
                label="Chart Type" 
                value={chart.chart_type.charAt(0).toUpperCase() + chart.chart_type.slice(1)} 
              />
              <InfoRow 
                icon={Eye} 
                label="Views" 
                value={chart.view_count || 0} 
              />
              <InfoRow 
                icon={Calendar} 
                label="Created" 
                value={formatDate(chart.created_at)} 
              />
              <InfoRow 
                icon={Clock} 
                label="Last Modified" 
                value={formatDate(chart.last_modified_at)} 
              />
            </CardContent>
          </Card>

          {/* 14a-3: the dashboards this chart is on (GET /charts/{id} carries used_in_dashboards) */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <LayoutDashboard className="w-4 h-4 text-blue-500" />
                <CardTitle>Used in dashboards</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {Array.isArray(chart.used_in_dashboards) && chart.used_in_dashboards.length > 0 ? (
                <ul className="space-y-1.5 text-sm">
                  {chart.used_in_dashboards.map((d) => (
                    <li key={d.id} className="truncate">
                      <Link to={`/dashboards/${d.id}`} className="text-white underline-offset-2 hover:underline">{d.title || `Dashboard ${d.id}`}</Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-[#6a6a6a]">Not on any dashboard yet.</p>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Download Section */}
<div>
  <p className="text-xs text-[#6a6a6a] mb-2">Download</p>
  <div className="space-y-2">
    {!tabular && (
      <Button 
        variant="secondary" 
        className="w-full justify-start"
        onClick={downloadPng}
      >
        <Download className="w-4 h-4" />
        Download PNG
      </Button>
    )}
    <Button 
      variant="secondary" 
      className="w-full justify-start"
      onClick={() => alert('PDF export coming soon')}
      disabled
    >
      <Download className="w-4 h-4" />
      Download PDF
    </Button>
    <Button 
      variant="secondary" 
      className="w-full justify-start"
      onClick={downloadCsv}
      disabled={!isBuilderChart(chart)}
      title={isBuilderChart(chart) ? undefined : 'Available once this chart is in the new builder'}
    >
      <Download className="w-4 h-4" />
      Download CSV
    </Button>
  </div>
  {downloadError && <p role="alert" className="mt-2 text-xs text-red-400">{downloadError}</p>}
</div>

              {/* Share Section */}
              <div className="pt-3 border-t border-[#2a2a2a]">
                <p className="text-xs text-[#6a6a6a] mb-2">Share</p>
                <div className="space-y-2">
                  <Button 
                    variant="secondary" 
                    className="w-full justify-start"
                    onClick={handleShare}
                >
                    <Share2 className="w-4 h-4" />
                    Share & Embed
                </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Schedule Export Button */}
<Card>
  <CardHeader>
    <CardTitle>Schedule Export</CardTitle>
  </CardHeader>
  <CardContent>
    <Button 
      className="w-full"
      onClick={() => setShowScheduleModal(true)}
    >
      <Clock className="w-4 h-4" />
      Manage Schedules
    </Button>
    <p className="text-xs text-[#6a6a6a] mt-3 text-center">
      Automatically export this chart via email
    </p>
  </CardContent>
</Card>

          {/* Data Source */}
          <Card>
            <CardHeader>
              <CardTitle>Data Source</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-[#6a6a6a] mb-1">Connector</p>
                <p className="text-sm text-white">Config #{chart.connector_config_id}</p>
              </div>
              <div>
                <p className="text-xs text-[#6a6a6a] mb-1">Entity</p>
                <p className="text-sm text-white">{chart.entity}</p>
              </div>
              <div>
                <p className="text-xs text-[#6a6a6a] mb-1">Metric</p>
                <p className="text-sm text-white">{chart.metric}</p>
              </div>
              <div>
                <p className="text-xs text-[#6a6a6a] mb-1">Time Grain</p>
                <p className="text-sm text-white">
                  {chart.grain === 'day' && 'Daily (30 days)'}
                  {chart.grain === 'week' && 'Weekly (26 weeks)'}
                  {chart.grain === 'month' && 'Monthly (12 months)'}
                </p>
              </div>

              {/* PHASE 13.4: Display Filters */}
              {((chart.filters && chart.filters.length > 0) || chart.group_by_field) && (
                <div className="pt-3 border-t border-[#2a2a2a]">
                  <p className="text-xs text-[#6a6a6a] mb-2">Filters & Grouping</p>
                  <FiltersDisplay
                    filters={chart.filters || []}
                    filterLogic={chart.filter_logic}
                    groupByField={chart.group_by_field}
                  />
                </div>
              )}
            </CardContent>
          </Card>


          {/* Organization */}
          {(chart.folder_id || chart.tags?.length > 0) && (
            <Card>
              <CardHeader>
                <CardTitle>Organization</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {chart.folder_id && (
                  <div className="flex items-center gap-2">
                    <Folder className="w-4 h-4 text-[#6a6a6a]" />
                    <span className="text-sm text-white">Folder #{chart.folder_id}</span>
                  </div>
                )}
                {chart.tags && chart.tags.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Tag className="w-4 h-4 text-[#6a6a6a]" />
                      <span className="text-xs text-[#6a6a6a]">Tags</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {chart.tags.map(tag => (
                        <Badge key={tag} variant="neutral">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      {/* Modals */}
<ScheduleExportModal
  isOpen={showScheduleModal}
  onClose={() => setShowScheduleModal(false)}
  chartId={id}
  chartTitle={chart?.title || 'Chart'}
/>

<ShareModal
  isOpen={showShareModal}
  onClose={() => setShowShareModal(false)}
  chartId={id}
  chartTitle={chart?.title || 'Chart'}
  visibility={chart?.visibility || 'private'}
/>

<ConfirmDialog
  isOpen={confirmDialog.isOpen}
  onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
  onConfirm={confirmDialog.onConfirm}
  title={confirmDialog.title}
  message={confirmDialog.message}
  confirmText="Delete"
  cancelText="Cancel"
  variant="danger"
/>

<ChartInUseDialog request={inUse} busy={deleting} onCancel={() => setInUse(null)}
  onConfirm={() => { setInUse(null); runDelete(true); }} />

<AddToDashboardDialog open={showAddToDashboard} onClose={() => setShowAddToDashboard(false)} chart={chart ? { ...chart, id: Number(id) } : null}
  onAdded={(d) => setChart((c) => (c ? { ...c, used_in_dashboards: [...(c.used_in_dashboards || []).filter((x) => x.id !== d.id), { id: d.id, title: d.title }].sort((a, b) => a.title.localeCompare(b.title)) } : c))} />
    </div>
  );
}

// ==========================================
// INFO ROW COMPONENT
// ==========================================
function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#2a2a2a] last:border-0">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#6a6a6a]" />
        <span className="text-sm text-[#a0a0a0]">{label}</span>
      </div>
      <span className="text-sm font-medium text-white">{value}</span>
    </div>
  );
}
    

    

// ==========================================
// HELPER FUNCTIONS
// ==========================================
function formatDate(isoString) {
  if (!isoString) return 'Never';
  
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'Unknown';
  }
}